// components/portal/OrganisationSelector.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building, ChevronDown } from '@/components/ui/lucide-memo';
import clsx from 'clsx';
import { useKernelStore } from '@/kernel/kernelStores';
import { GenericEntitySelector } from './GenericEntitySelector';

interface OrganisationSelectorProps {
  selectedOrgId: string | null;
  onSelect: (orgId: string | null) => void;
  disabled?: boolean;
  /** ChatGPT's labelled picker ('All organisations ⌄'), for a page header. */
  pill?: boolean;
  /** Offer 'All organisations' (a filter); off where exactly one must be chosen (Settings' workspace). */
  allowAll?: boolean;
}

const translations = {
  en: { allOrgs: 'All organisations', selectOrg: 'Select organisation' },
  fr: { allOrgs: 'Toutes les organisations', selectOrg: 'Selectionner une organisation' },
};

const Trigger = React.memo(function Trigger({
  selectedOrgId,
  disabled,
  imgError,
  onImageError,
  fallbackLabel,
  openSelector,
  onContextMenu,
  pill,
}: {
  pill?: boolean;
  selectedOrgId: string | null;
  disabled?: boolean;
  imgError: boolean;
  onImageError: () => void;
  fallbackLabel: string;
  openSelector: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}) {
  const orgName = useKernelStore((state) => (selectedOrgId ? state.inventory.Organisation.registry[selectedOrgId]?.name : undefined));
  const label = orgName || fallbackLabel;
  const logoSrc = selectedOrgId ? `/api/organisation/logo?orgId=${selectedOrgId}` : null;
  const hasLogo = !!selectedOrgId && !imgError;

  const onClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    openSelector();
  }, [disabled, openSelector]);

  if (pill) {
    return (
      <button
        type="button"
        onClick={onClick}
        onContextMenu={onContextMenu}
        disabled={disabled}
        className="inline-flex h-8 max-w-[220px] shrink-0 items-center gap-2 rounded-full px-3 text-[#E5E5E5] transition-colors hover:bg-white/[0.08] hover:text-white disabled:opacity-50"
        style={{ fontSize: 14 }}
      >
        {hasLogo ? <img src={logoSrc!} alt="" className="h-5 w-5 shrink-0 rounded-full object-cover" onError={onImageError} /> : null}
        <span className="truncate" style={{ fontSize: 'inherit' }}>{label}</span>
        <ChevronDown size={16} className="shrink-0 text-[#AFAFAF]" />
      </button>
    );
  }

  return (
    <div
      onClick={onClick}
      onContextMenu={onContextMenu}
      title={label}
      className={clsx(
        'flex items-center shrink-0',
        disabled ? 'cursor-default' : 'cursor-pointer group/org',
      )}
    >
      {hasLogo ? (
        <div className={clsx(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors',
          !disabled && 'group-hover/org:bg-white/[0.08]'
        )}>
          <img
            src={logoSrc!}
            alt={label}
            className="h-6 w-6 rounded-full object-cover"
            onError={onImageError}
          />
        </div>
      ) : (
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/[0.08]">
          <Building size={18} strokeWidth={2} className={clsx('transition-colors', disabled ? 'text-white/40' : 'text-[#E5E5E5] group-hover/org:text-white')} />
        </div>
      )}
    </div>
  );
});

export const OrganisationSelector = React.memo(({ selectedOrgId, onSelect, disabled, pill, allowAll = true }: OrganisationSelectorProps) => {
  const lang = useKernelStore((state) => state.language);
  const [imgError, setImgError] = useState(false);
  const t = translations[lang];

  useEffect(() => {
    setImgError(false);
  }, [selectedOrgId]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (selectedOrgId && onSelect) {
      e.preventDefault();
      e.stopPropagation();
      onSelect(null);
    }
  }, [selectedOrgId, onSelect]);

  const selectedIds = useMemo(() => (selectedOrgId ? [selectedOrgId] : []), [selectedOrgId]);

  const handleClearSelection = useCallback(() => onSelect(null), [onSelect]);

  const handleImageError = useCallback(() => setImgError(true), []);

  const renderTrigger = useCallback((_items: unknown[], openSelector: () => void) => (
    <Trigger
      selectedOrgId={selectedOrgId}
      disabled={disabled}
      imgError={imgError}
      onImageError={handleImageError}
      fallbackLabel={t.allOrgs}
      openSelector={openSelector}
      onContextMenu={handleContextMenu}
      pill={pill}
    />
  ), [selectedOrgId, disabled, imgError, handleImageError, t.allOrgs, handleContextMenu, pill]);

  return (
    <GenericEntitySelector
      type="Organisation"
      selectedIds={selectedIds}
      onSelect={onSelect}
      variant="row"
      title={t.selectOrg}
      disabled={disabled}
      onClearSelection={allowAll ? handleClearSelection : undefined}
      renderTrigger={renderTrigger}
    />
  );
});

OrganisationSelector.displayName = 'OrganisationSelector';
